/**
 * A Spinner component that will report REPORT_PERCENT percent of timeouts to
 * Sentry and optionally display apologetic messages to the user and ask them to
 * refresh. This component uses the WonderBlocks spinner.
 */

import {t} from "@lingui/core/macro";
import {StyleSheet} from "aphrodite";
import {compose} from "mu-lambda";
import * as React from "react";

import {Text, View, type StyleType} from "@khanacademy/wonder-blocks-core";
import {CircularSpinner} from "@khanacademy/wonder-blocks-progress-spinner";
import {
    withActionScheduler,
    type WithActionSchedulerProps,
    type WithoutActionScheduler,
} from "@khanacademy/wonder-blocks-timing";
import {color, spacing} from "@khanacademy/wonder-blocks-tokens";
import type {Metadata} from "@khanacademy/wonder-stuff-core";
import Log, {Errors} from "@khan/logging";

import ReportError from "#app-shell-package/errors/report-error.tsx";
import withUserProfile, {
    type WithoutUserProfile,
    type WithUserProfileProps,
} from "#data-access/with-user-profile.tsx";
import type {SentryTeam} from "#shared-package/types.ts";

const SECOND_TIMEOUT_MULTIPLIER = 2;

// allow to send reports to 10% of the users
const shouldReport = Math.random() < 0.1;

type DefaultProps = {
    // WonderBlocks Spinner Props
    /**
     * The size of the spinner. (large = 96px, medium = 48px, small = 24px,
     * xsmall = 16px)
     */
    size: "xsmall" | "small" | "medium" | "large";
    /**
     * Should a light version of the spinner (and optional notification text)
     * be shown?
     * (To be used on a dark background.)
     */
    light: boolean;
    /**
     * Number of seconds after which we should report a timeout error to Sentry
     * and (optionally) alert the user if `notifyUser` is set.
     *
     * Default: 15 seconds
     */
    timeoutSeconds: number;
};

type Props = DefaultProps & {
    // Props specific to TimeoutSpinner
    /** A unique ID for identifying timeouts in Sentry */
    sentryId: string;
    /** A short description of what this spinner is waiting on */
    description: string;
    /**
     * The team that owns the code that displays this spinner. This used for
     * breaking out errors in Sentry and therefore should match the teams
     * defined in Sentry.
     */
    sentryTeam: SentryTeam;
    /** Any additional info to be logged to Sentry when reporting a timeout */
    sentryExtras?: Metadata;
    /**
     * (optional) Display an apologetic message if the timeout is hit. If the
     * spinner is still displaying after 2 * timeoutSeconds, the user is
     * instructed to refresh the page.
     */
    notifyUser?: boolean;
    /**
     * Any (optional) styling to apply to the spinner
     */
    style?: StyleType;
} & WithUserProfileProps &
    WithActionSchedulerProps;

type State = {
    firstTimeoutHit: boolean;
    secondTimeoutHit: boolean;
};

/**
 * @deprecated Use CircularSpinner from @khanacademy/wonder-blocks-progress-spinner.
 */
class DeprecatedTimeoutSpinner extends React.Component<Props, State> {
    _mounted: boolean;
    // @ts-expect-error - TS2564 - Property 'timeoutId' has no initializer and is not definitely assigned in the constructor.
    timeoutId: number;

    static defaultProps: DefaultProps = {
        size: "large",
        light: false,
        timeoutSeconds: 15,
    };

    constructor(props: Props) {
        super(props);
        this.state = {
            firstTimeoutHit: false,
            secondTimeoutHit: false,
        };
        this._mounted = false;
    }

    componentDidMount() {
        this._mounted = true;
        const {sentryId, sentryTeam, timeoutSeconds, schedule} = this.props;

        // Create a unique timeout ID so that we can can reliably tie first
        // and second timeouts together during analysis
        const timeoutId = `${sentryId}|${Date.now()}|${Math.floor(
            Math.random() * 100000,
        )}`;

        const onFirstTimeout = () => {
            if (this._mounted) {
                this.setState({
                    firstTimeoutHit: true,
                });
            }
            this.logTimeout(
                `Spinner timeout: first (${sentryTeam})`,
                timeoutSeconds,
                timeoutId,
            );
            schedule.timeout(onSecondTimeout, timeoutSeconds * 1000);
        };

        const onSecondTimeout = () => {
            if (this._mounted) {
                this.setState({
                    secondTimeoutHit: true,
                });
            }

            // Log that we've timed out a second time, and thus the user has
            // waited timeoutSeconds * SECOND_TIMEOUT_MULTIPLIER seconds total.
            this.logTimeout(
                `Spinner timeout: second (${sentryTeam})`,
                timeoutSeconds * SECOND_TIMEOUT_MULTIPLIER,
                timeoutId,
            );
        };

        schedule.timeout(onFirstTimeout, timeoutSeconds * 1000);
    }

    componentWillUnmount() {
        this._mounted = false;
    }

    logTimeout(message: string, timeoutSeconds: number, timeoutId: string) {
        const {description, profile, sentryExtras, sentryId} = this.props;
        const user = profile?.get("nickname");

        const metadata = {
            ...sentryExtras,
            sentryId,
            description,
            timeoutSeconds,
            user,
            timeoutId,
        } as const;

        // Throttle our reporting to avoid overwhelming ourselves/Sentry.
        if (shouldReport) {
            // TODO(somewhatabstract): Report this to our event logging rather
            // than to Sentry once that's up and running.
            Log.error(message, Errors.Internal, {
                sentryData: {
                    contexts: {
                        "Timeout Details": metadata,
                    },
                },
            });
        } else {
            Log.log(message, metadata);
        }
    }

    render(): React.ReactNode {
        const {notifyUser, size, light, style} = this.props;
        const {firstTimeoutHit, secondTimeoutHit} = this.state;
        const textColor = light ? color.white64 : color.offBlack;

        return (
            <View style={[styles.containerDefault, style]}>
                <CircularSpinner size={size} light={light} />
                {firstTimeoutHit && notifyUser && (
                    // TODO(jvoll): As we use this with different sized spinners
                    // we'll want to scale the font size in relation to the
                    // spinner size.
                    <Text style={[styles.userNotification, {color: textColor}]}>
                        {secondTimeoutHit ? (
                            <ReportError />
                        ) : (
                            t`We're sorry this is taking so long...`
                        )}
                    </Text>
                )}
            </View>
        );
    }
}

const styles = StyleSheet.create({
    containerDefault: {
        alignItems: "center",
    },
    userNotification: {
        marginTop: spacing.xSmall_8,
        marginBottom: spacing.xSmall_8,
        fontSize: 16,
    },
});

type ExportProps = WithoutActionScheduler<
    WithoutUserProfile<
        JSX.LibraryManagedAttributes<
            typeof DeprecatedTimeoutSpinner,
            React.ComponentProps<typeof DeprecatedTimeoutSpinner>
        >
    >
>;

export default compose(
    withActionScheduler,
    withUserProfile,
)(DeprecatedTimeoutSpinner) as React.ComponentType<ExportProps>;
